import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Input } from 'reactstrap';

import BasicTable from 'components/layout/tables/BasicTable';
import { withContentWidths } from 'services/tables/getColumnWidths';
import translations from 'services/translations/translations';
import customerApi, {
  IContactPersonRow,
  IContactPersonSearch,
} from '../../api/contacts';

// The searches are sent when the user stops typing
const SEARCH_DELAY = 400;

/**
 * The persons tab of the contacts: the contact persons of all the companies, each column
 * with its search below its name (any part of the text). A row opens the person.
 */
const ContactPersonsTab = (): JSX.Element => {
  const { t } = useTranslation([translations.contacts, translations.common]);
  const history = useHistory();

  const [rows, setRows] = useState<IContactPersonRow[]>([]);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [totalSize, setTotalSize] = useState(0);
  const [selectedRow, setSelectedRow] = useState<number | undefined>();
  // As typed, and as sent after the user stopped typing
  const [search, setSearch] = useState<IContactPersonSearch>({});
  const [applied, setApplied] = useState<IContactPersonSearch>({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      const sent: IContactPersonSearch = Object.fromEntries(
        Object.entries(search).filter(([, value]) => value !== '')
      );
      // E.g. "kaikki" finds the persons common to all the areas
      const office = sent.office?.trim().toLowerCase();
      if (
        office &&
        office.length >= 3 &&
        t('All areas').toLowerCase().startsWith(office)
      ) {
        sent.office = 'all';
      }
      setApplied(sent);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let active = true;
    customerApi
      .searchPersons({
        ...applied,
        limit: sizePerPage,
        offset: (page - 1) * sizePerPage,
      })
      .then((result) => {
        if (active) {
          setRows(result.rows);
          setTotalSize(result.count);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [applied, page, sizePerPage]);

  useEffect(() => {
    if (selectedRow !== undefined && rows[selectedRow] !== undefined) {
      history.push(`/contacts/persons/${rows[selectedRow].id}`);
    }
  }, [history, rows, selectedRow]);

  // A click on a search does not open the person
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  const header =
    (key: keyof IContactPersonSearch) =>
    ({ text }: { text: string }): JSX.Element =>
      (
        <div className="contacts-column-header">
          <span>{text}</span>
          <Input
            aria-label={text}
            bsSize="sm"
            onChange={(event) => {
              // Read before the event is reused (React 16)
              const { value } = event.target;
              setSearch((current) => ({ ...current, [key]: value }));
            }}
            onClick={stop}
            placeholder={t('Search column')}
            type="search"
            value={search[key] ?? ''}
          />
        </div>
      );

  const columns = [
    { dataField: 'firstName', text: t('First Name') },
    { dataField: 'lastName', text: t('Last Name') },
    { dataField: 'companyName', text: t('Company') },
    { dataField: 'title', text: t('Title') },
    { dataField: 'jobDescription', text: t('Job Description') },
    {
      dataField: 'office',
      // Common to all the areas when the person has no department
      formatter: (office: null | string) => office ?? t('All areas'),
      text: t('Office'),
    },
    { dataField: 'operatingArea', text: t('Operating area') },
    { dataField: 'email', text: t('Email') },
    {
      dataField: 'phone',
      formatter: (phone: null | string, row: IContactPersonRow) =>
        phone || row.phoneWithCountryCode || '',
      text: t('Phone'),
    },
  ].map((column) => ({
    ...column,
    headerFormatter: header(column.dataField as keyof IContactPersonSearch),
  }));

  // The widths of the columns follow their content
  const sized = withContentWidths(columns, rows);

  return (
    <>
      <div
        className="table-scroll"
        style={{ '--table-min-width': sized.minWidth } as React.CSSProperties}
      >
        <BasicTable
          columns={sized.columns}
          data={rows}
          handleTableChange={(
            type: unknown,
            changed: { page: number; sizePerPage: number }
          ) => {
            setPage(changed.page);
            setSizePerPage(changed.sizePerPage);
          }}
          keyField="id"
          page={page}
          setSelectedRow={setSelectedRow}
          sizePerPage={sizePerPage}
          totalSize={totalSize}
        />
      </div>
    </>
  );
};

export default ContactPersonsTab;
